import React, { useState, useMemo, useEffect } from "react";
import HeroSection from "@/components/HeroSection";
import { blogPosts } from "@/data/blog";
import BlogNavigation from "@/components/blog/BlogNavigation";
import BlogSearchBar from "@/components/blog/BlogSearchBar";
import SEOMetadata from "@/components/SEOMetadata";
import BlogCard from "@/components/blog/BlogCard";
import BlogFAQComponent from "@/components/blog/BlogFAQComponent";
import { Helmet } from "react-helmet";
import { webPageStructuredData } from "@/data/structuredData";
// TagNavigation removed to avoid 404 links on blog index
import { useBlogAnalytics } from "@/hooks/useBlogAnalytics";
import { useSearchParams } from "react-router-dom";

// Enhanced German date parser that handles both formats: DD.MM.YYYY and "DD. Month YYYY"
const parseGermanDate = (dateString: string): Date | null => {
  // Handle special case
  if (dateString === "In Vorbereitung") return null;
  
  try {
    // Handle format: "04. März 2025"
    if (dateString.includes(' ')) {
      const parts = dateString.split(' ');
      if (parts.length !== 3) return null;
      
      // Extract day (remove trailing period)
      const day = parseInt(parts[0].replace('.', '').trim(), 10);
      
      // Map German month names to numbers
      const monthMap: Record<string, number> = {
        'Januar': 0, 'Februar': 1, 'März': 2, 'April': 3, 
        'Mai': 4, 'Juni': 5, 'Juli': 6, 'August': 7,
        'September': 8, 'Oktober': 9, 'November': 10, 'Dezember': 11
      };
      
      const month = monthMap[parts[1]];
      if (month === undefined) return null;
      
      const year = parseInt(parts[2], 10);
      
      // Validate parsed values
      if (isNaN(day) || isNaN(year)) return null;
      
      const date = new Date(year, month, day);
      
      // Verify date validity
      if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
        console.warn(`Invalid date created from: ${dateString}`);
        return null;
      }
      
      return date;
    } 
    // Handle standard format: DD.MM.YYYY
    else {
      const parts = dateString.split('.');
      if (parts.length !== 3) return null;
      
      // Ensure we're working with trimmed strings and properly parsed integers
      const day = parseInt(parts[0].trim(), 10);
      const month = parseInt(parts[1].trim(), 10) - 1; // JS months are 0-indexed
      const year = parseInt(parts[2].trim(), 10);
      
      // Validate parsed values are actual numbers
      if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
      
      // Create date and validate it's a valid date object
      const date = new Date(year, month, day);
      
      // Ensure the date is valid (important check for edge cases like February 30)
      if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
        console.warn(`Invalid date created from: ${dateString}`);
        return null;
      }
      
      return date;
    }
  } catch (error) {
    console.error("Error parsing date:", dateString, error);
    return null;
  }
};

const Blog = () => {
  
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilterFromUrl = searchParams.get('filter') || 'all';
  const [selectedFilter, setSelectedFilter] = useState<string>(initialFilterFromUrl);
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  // Initialize blog analytics
  const { trackLocalSearch } = useBlogAnalytics({
    trackBlogViews: true,
    trackTagPerformance: true
  });

  // Scroll to top when navigating to blog page (unless search is active)
  useEffect(() => {
    if (!searchParams.get('search')) {
      window.scrollTo(0, 0);
    }
  }, []);

  // Sync selected filter with URL (?filter=...); use replace to avoid polluting history
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (selectedFilter === 'all') {
      params.delete('filter');
    } else {
      params.set('filter', selectedFilter);
    }
    setSearchParams(params, { replace: true });
  }, [selectedFilter, setSearchParams]);
  
  // Enhanced filtering with category-based filtering (newest first)
  const filteredPosts = useMemo(() => {
    let filtered = [...blogPosts];

    // Apply search filter first
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchLower) ||
        post.excerpt.toLowerCase().includes(searchLower) ||
        post.tags?.some(tag => tag.toLowerCase().includes(searchLower)) ||
        post.category.toLowerCase().includes(searchLower)
      );
    }

    // Apply navigation filter with precise keyword matching
    if (selectedFilter !== "all") {
      const navigationFilters: Record<string, {
        categories?: string[];
        keywords?: string[];
      }> = {
        coaching: {
          categories: ["Coaching-Philosophie", "Coaching-Methoden"],
          keywords: ["outdoor-coaching", "neuroplastizität", "epigenetik", "coaching-philosophie", "lebensphilosophie", "weisheit", "expertise", "persönliche entwicklung", "haltung", "denkweise"]
        },
        finanzen: {
          categories: ["Finanzwissen", "Finanzcoaching"],
          keywords: ["kostolany", "börse", "anlagestrategie", "finanzcoaching", "finanzberatung", "geldanlage", "investment", "gesundes verhältnis"]
        },
        technologie: {
          categories: ["Technologie & Systeme"],
          keywords: ["künstliche intelligenz", "webentwicklung", "lovable", "technologie", "digitalisierung", "innovation", "ki-tools", "automatisierung", "krypto", "blockchain", "steuer"]
        },
        psychologie: {
          categories: ["Finanzpsychologie"],
          keywords: ["musik", "emotionen", "social media", "psychologie", "verhalten", "stimmung", "neuronale balance", "entscheidungen", "finanzpsychologie"]
        },
        fuehrung: {
          categories: ["Executive Coaching", "Führung"],
          keywords: ["führung", "executive coaching", "leadership", "führungskräfte", "business coaching", "führungsstil", "führungsexzellenz", "digitaler humanismus", "kommunikation", "mindful leadership"]
        }
      };

      const config = navigationFilters[selectedFilter];
      if (config) {
        filtered = filtered.filter(post => {
          // Suche in Kategorien
          if (config.categories && config.categories.includes(post.category)) {
            return true;
          }
          
          // Suche in Keywords (Title, Excerpt, Tags)
          if (config.keywords) {
            const searchText = `${post.title} ${post.excerpt} ${post.tags?.join(" ") || ""}`.toLowerCase();
            return config.keywords.some(keyword => 
              searchText.includes(keyword.toLowerCase())
            );
          }
          
          return false;
        });
      }
    }

    // Sort by date (newest first) - parse German dates
    filtered.sort((a, b) => {
      const dateA = parseGermanDate(a.date);
      const dateB = parseGermanDate(b.date);
      
      if (!dateA || !dateB) return 0;
      
      return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
    });

    return filtered;
  }, [selectedFilter, searchTerm]);

  return (
    <main className="min-h-screen">
      <SEOMetadata
        metaTitle="Blog: Coaching, Führung & Finanzpsychologie | Jörg Hörmann"
        metaDescription="Blog über Führung, Finanzpsychologie und persönliche Entwicklung. Praxisnahe Impulse für Führungskräfte und Privatpersonen von Jörg Hörmann."
        canonicalUrl="https://joerg-hoermann.de/blog"
        title="Blog: Wissenswertes rund um Coaching & Finanzpsychologie"
        description="Gedanken zu Führung, Transformation und Finanzen. Qualität statt Quantität."
        pageName="Blog"
        primaryLink="/blog"
        keywords="Coaching Blog, Finanzcoaching, Geldmindset, Finanzpsychologie, Outdoor-Coaching, Coaching-Methoden, Persönlichkeitsentwicklung"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData({
            name: "Blog: Coaching, Führung & Finanzpsychologie",
            description: "Blog über Führung, Finanzpsychologie und persönliche Entwicklung. Praxisnahe Impulse von Jörg Hörmann.",
            url: "https://joerg-hoermann.de/blog"
          }))}
        </script>
      </Helmet>
      
      <HeroSection
        title="Blog: Impulse für Ihre Entwicklung"
        subtitle="Coaching & Finanzpsychologie"
        description="Hier teile ich regelmäßig Wissen und Erfahrungen aus meiner Coaching-Praxis. Von Finanzpsychologie über Outdoor-Coaching bis hin zu modernen Methoden der Persönlichkeitsentwicklung."
        backgroundClass="bg-joerg-50"
        imageUrl="/images/blog/notizen-planung.jpg"
        pageName="Blog"
      />

      {/* AI-Summary / AEO Abstract */}
      <section className="sr-only" aria-label="Zusammenfassung" data-speakable="summary">
        <p>Der Blog von Jörg Hörmann bietet praxisnahe Impulse zu Führung, Finanzpsychologie und persönlicher Entwicklung. Themen reichen von Katalysator-Führung und emotionaler Intelligenz bis hin zu Verhaltensökonomie und Outdoor-Coaching – wissenschaftlich fundiert und aus der Coaching-Praxis.</p>
      </section>

      <section className="py-16 md:py-20 bg-white" id="blog-content">
        <div className="container">
          <BlogSearchBar 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
          
          <BlogNavigation 
            posts={blogPosts}
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
          />
          
          {/* Blog Post Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {filteredPosts.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
          
          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg text-joerg-600">Keine Artikel für diese Kategorie gefunden. Bitte wählen Sie ein anderes Thema.</p>
            </div>
          )}
        </div>
      </section>
      
      <BlogFAQComponent />
    </main>
  );
};

export default Blog;